// SheetWorkspace.js
//
// The sheet workspace (Spec 2 section 6.6): one sheet of a workbook drawn with its real
// formatting, the tables Decode found on it painted over the grid, and a rail on the right
// for changing them. Everything here edits a DRAFT of the sheet's plan, the value of the
// sheet's Segment decision:
//
//     plan = { fields: { 'Reporting Month': 'Jan 2026' },
//              regions: [{ region_id, name, header_first_row, header_last_row,
//                          body_first_row, body_last_row, col_first, col_last,
//                          exclude_rows, section_values, unpivot, approximate }] }
//
// Nothing is written until Save, which is one Segment edit of the whole plan. A whole
// plan replaces the saved one, so a table someone else added meanwhile would be deleted
// with its unit: the save therefore carries the `made_at` of the saved version the draft
// was seeded from, and the gesture refuses when the saved version has changed. That
// version is kept as the draft's seed until the person reloads: a save someone else makes
// meanwhile never becomes the draft's seed on its own, so the gesture keeps refusing the
// draft's Save until the person reloads (Spec 2 section 6.6).
//
//     open      seed the draft from the saved plan, or take back a draft set aside earlier
//     edit      change the draft only; a drag over the sheet sets the focused table's bounds
//     another version saved   keep the draft and its seed, with a notice offering Reload
//     Save      editFileDecisions(segment, [{ key: sheet, value: plan }], expectedVersions)
//       refused     keep the draft
//       accepted    wait for Decode to apply it:
//                     applied     close
//                     refused     keep the draft, show Decode's reason
//
// "Ignore this table" / "Use this table" and "Read this sheet again" are not part of the
// draft: each is its own gesture, written at once.
//
// When the file stops being editable while the workspace is open, by anything but the
// workspace's own gesture (someone else's change, Decode's own work, a deletion), the
// workspace closes with a notice (Spec 2 sections 6.4 and 6.6, 00 N.4). A draft with
// changes is handed to the host first (onSetAside), which gives it back the next time this
// sheet is opened: it is never saved on its own, and comes back with its seed, so a save
// made meanwhile still stops it until the person reloads.

import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import {
  NO_INGEST_UNIT_LEFT,
  REWIND_CONFIRMATION,
  editFileDecisions,
  gestureFailureMessage,
  needsRewindConfirmation,
  requestFile,
  setDisposition,
} from '../../../services/cleaningGestures';
import { activeVersion, isPending, versionsOf } from '../../../utils/decisions';
import { fileEditBlock, UNIT_LABEL } from '../../../utils/ingestionStatus';
import { versionToken } from '../../../utils/blobPaths';
import { columnIndexToLetter, sheetPlanProblems } from '../../../utils/sheetPlans';
import { toMillis } from '../../../utils/timestamps';
import SheetViewer, { regionColour, withAlpha } from './SheetViewer';

// The backend's limit on a table's rows once its period columns are turned into rows
// (presplit.ROW_LIMIT), which validate_person_plan refuses a person's plan past.
const UNPIVOT_ROW_LIMIT = 50000;

const SEGMENT = 'segment';
const DISPOSITIONS = 'dispositions';
// One identity for "no tables", so the viewer's overlay is not rebuilt on every render.
const NO_REGIONS = [];
const SAVED_BY_SOMEONE_ELSE = 'This sheet was saved by someone else while you were editing it. '
  + 'Your changes are still here: Reload starts again from what they saved.';
const REFUSED_WITHOUT_REASON = 'Decode could not use these tables. Your changes are still here.';
const DRAFT_TAKEN_BACK = 'These are the changes you had not saved when this sheet was last closed. '
  + 'Check them against the sheet before saving, or Reload to start again from the saved plan.';

// ── The checks a draft must pass before Save ────────────────────────────────────────

// Every reason the draft cannot be saved, in the order the rail lists them: the plan's
// own (utils/sheetPlans.js, which the Segment edit gesture and the backend repeat); a
// table whose rows run past the sheet's last row, once the sheet has loaded; a table
// whose period columns would expand past the row limit (the backend refuses it too);
// then the header fields. These are edited as a list of rows, so two may have the same
// name for a moment, which the plan's map cannot hold. Empty when the draft can be saved.
export function draftProblems(draft, lastRow = null) {
  const problems = sheetPlanProblems(draftToPlan(draft));
  draft.regions.forEach((region, index) => {
    const label = `Table ${index + 1}`;
    if (lastRow && region.body_last_row > lastRow) {
      problems.push(`${label}: its rows end after the last row of the sheet (row ${lastRow}).`);
    }
    if (region.unpivot) {
      const excludedInBody = new Set((region.exclude_rows || []).filter((row) => (
        row >= region.body_first_row && row <= region.body_last_row
      )));
      const bodyRows = region.body_last_row - region.body_first_row + 1 - excludedInBody.size;
      const periodCount = Object.keys(region.unpivot.labels || {}).length;
      if (bodyRows * periodCount > UNPIVOT_ROW_LIMIT) {
        problems.push(`${label} expands to more than ${UNPIVOT_ROW_LIMIT.toLocaleString()} rows. `
          + 'Narrow the rows of this table, or keep its columns as they are.');
      }
    }
  });
  const seenKeys = new Set();
  draft.fields.forEach((field) => {
    const key = field.key.trim();
    if (!key) {
      problems.push('Every header field needs a name.');
      return;
    }
    if (seenKeys.has(key)) problems.push(`Two header fields are both called '${key}'.`);
    seenKeys.add(key);
  });
  return [...new Set(problems)];
}

// ── The draft ───────────────────────────────────────────────────────────────────────
// The header fields are edited as a list of rows, not as the plan's map: a key being
// typed may collide with another for a moment, and a map would lose one of them. Each
// row carries an id of its own, only so React can tell the rows apart.

let nextFieldRowId = 0;

function newFieldRow(key, value) {
  nextFieldRowId += 1;
  return { rowId: `field-${nextFieldRowId}`, key, value };
}

export function draftToPlan(draft) {
  return {
    fields: Object.fromEntries(draft.fields.map((field) => [field.key, field.value])),
    regions: draft.regions,
  };
}

function samePlan(left, right) {
  const normalise = (plan) => ({ fields: plan?.fields || {}, regions: plan?.regions || [] });
  return JSON.stringify(normalise(left)) === JSON.stringify(normalise(right));
}

function draftIsDirty(state) {
  if (!state.draft || !state.seed) return false;
  return !samePlan(draftToPlan(state.draft), state.seed.plan);
}

// A draft of the saved version's plan, with that version as its seed.
function seededFrom(liveVersion) {
  if (!liveVersion) return { draft: null, seed: null };
  const plan = liveVersion.value;
  return {
    draft: {
      fields: Object.entries(plan?.fields || {}).map(([key, value]) => newFieldRow(key, value)),
      regions: JSON.parse(JSON.stringify(plan?.regions || [])),
    },
    seed: { plan, madeAt: liveVersion.made_at },
  };
}

const INITIAL_STATE = { draft: null, seed: null, notice: null };

function workspaceReducer(state, action) {
  switch (action.type) {
    // A draft set aside when this sheet was last closed comes back as it was, with the
    // seed it was made from; otherwise the draft starts from the saved plan.
    case 'open':
      if (action.setAside) {
        return {
          draft: action.setAside.draft,
          seed: action.setAside.seed,
          notice: { severity: 'info', text: DRAFT_TAKEN_BACK, offerReload: true },
        };
      }
      return { ...seededFrom(action.live), notice: null };

    // The saved plan as it now stands, changed by anything but this workspace's own save.
    case 'saved-plan-changed': {
      const live = action.live;
      if (!live) return state;
      if (!state.seed) return { ...seededFrom(live), notice: null };
      const sameVersion = toMillis(state.seed.madeAt) === toMillis(live.made_at);
      if (sameVersion && samePlan(state.seed.plan, live.value)) return state;
      // An untouched draft simply follows the saved plan.
      if (!draftIsDirty(state)) return { ...seededFrom(live), notice: null };
      // Decode tidied the same saved version in place (its bounds clamped): the tidied plan
      // is the new baseline, quietly, and the draft keeps the version it can save over.
      if (sameVersion) return { ...state, seed: { ...state.seed, plan: live.value } };
      // Someone else saved another version. The draft keeps its seed, the version it was
      // made from, so the gesture refuses its Save until the person reloads.
      return { ...state, notice: { severity: 'warning', text: SAVED_BY_SOMEONE_ELSE, offerReload: true } };
    }

    // Decode refused this workspace's saved plan and made the version before it, the
    // draft's seed, the saved one again.
    case 'refused':
      return { ...state, notice: { severity: 'error', text: action.reason, offerReload: true } };

    // An explicit reload: the saved plan as it now stands is the draft and its seed.
    case 'reload':
      return { ...seededFrom(action.live), notice: null };

    case 'edit-region':
      return {
        ...state,
        draft: {
          ...state.draft,
          regions: state.draft.regions.map((region) => (
            region.region_id === action.regionId ? { ...region, ...action.patch } : region
          )),
        },
      };

    case 'replace-region':
      return {
        ...state,
        draft: {
          ...state.draft,
          regions: state.draft.regions.map((region) => (
            region.region_id === action.region.region_id ? JSON.parse(JSON.stringify(action.region)) : region
          )),
        },
      };

    case 'add-region':
      return { ...state, draft: { ...state.draft, regions: [...state.draft.regions, action.region] } };

    case 'remove-region':
      return {
        ...state,
        draft: {
          ...state.draft,
          regions: state.draft.regions.filter((region) => region.region_id !== action.regionId),
        },
      };

    case 'edit-field':
      return {
        ...state,
        draft: {
          ...state.draft,
          fields: state.draft.fields.map((field) => (
            field.rowId === action.rowId ? { ...field, ...action.patch } : field
          )),
        },
      };

    case 'add-field':
      return { ...state, draft: { ...state.draft, fields: [...state.draft.fields, newFieldRow('', '')] } };

    case 'remove-field':
      return {
        ...state,
        draft: { ...state.draft, fields: state.draft.fields.filter((field) => field.rowId !== action.rowId) },
      };

    case 'clear-notice':
      return { ...state, notice: null };

    default:
      throw new Error(`Unknown workspace action ${action.type}`);
  }
}

function parseRowList(text) {
  return text
    .split(',')
    .map((part) => parseInt(part.trim(), 10))
    .filter((n) => Number.isFinite(n));
}

// Props: open, companyId, tableId, file (the live file document, with its id), sheetName,
// onClose, onNotice(message) (the host's notice, for a workspace that closes itself); and
// optionally batch (the file's batch document, for the edit block), focusRegionId (the
// table to focus when the workspace opens), setAside ({ draft, seed }, a draft set aside
// when this sheet was last closed) and onSetAside(saved | null) (the host keeps a draft
// set aside, or forgets it once it is taken back).
export default function SheetWorkspace({
  open,
  companyId,
  tableId,
  file,
  sheetName,
  onClose,
  onNotice = () => {},
  batch = null,
  focusRegionId = null,
  setAside = null,
  onSetAside = () => {},
}) {
  const fileId = file?.id;
  const segmentRecord = file?.processes?.[SEGMENT] || null;
  const liveVersion = segmentRecord ? activeVersion(segmentRecord, sheetName) : null;
  const editBlock = file ? fileEditBlock(file, batch) : '';
  const sheetCopyPath = file?.assets?.sheet_copies?.[sheetName] || null;

  const [state, dispatch] = useReducer(workspaceReducer, INITIAL_STATE);
  const { draft, seed, notice } = state;
  const dirty = draftIsDirty(state);

  const [selected, setSelected] = useState(focusRegionId);
  const [selection, setSelection] = useState(null);
  const [extent, setExtent] = useState(null);
  // The "rows to leave out" field as typed, per table. Parsing on every keystroke would
  // drop a just-typed comma before the next number could follow it.
  const [excludeText, setExcludeText] = useState({});
  const [units, setUnits] = useState({ status: 'loading', list: [] });
  const [saving, setSaving] = useState(false);
  // This workspace's own gesture, from the moment it is sent until the file can be edited
  // again: the file being busy meanwhile is that gesture's work, which the workspace shows
  // rather than closing. { processId, startedAt, previousEditedAt, processed }: the
  // record the gesture edits, when it was sent, that record's edit before it, and whether
  // Decode has run since.
  const [ownWork, setOwnWork] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [nothingToIngestOpen, setNothingToIngestOpen] = useState(false);
  const [toast, setToast] = useState('');

  // Read inside effects that must not re-run on every file snapshot.
  const liveVersionRef = useRef(liveVersion);
  liveVersionRef.current = liveVersion;
  const fileRef = useRef(file);
  fileRef.current = file;
  const stateRef = useRef(state);
  stateRef.current = state;
  const hostRef = useRef({ onClose, onNotice, onSetAside, setAside });
  hostRef.current = { onClose, onNotice, onSetAside, setAside };

  // Text that changes exactly when the saved plan of this sheet does: its version, or the
  // version's value (Decode clamps a person's plan in place when it applies it).
  const liveKey = liveVersion
    ? `${toMillis(liveVersion.made_at)}|${liveVersion.made_by}|${JSON.stringify(liveVersion.value)}`
    : '';
  const ownRecord = ownWork ? file?.processes?.[ownWork.processId] : null;
  const ownRecordProcessed = Boolean(ownRecord)
    && toMillis(ownRecord.edited_at) > ownWork.previousEditedAt && !isPending(ownRecord);

  // Opening seeds the draft from the saved plan, or takes back a draft set aside, which
  // the host then forgets.
  useEffect(() => {
    if (!open) return;
    const { setAside: takenBack, onSetAside: forget } = hostRef.current;
    dispatch({ type: 'open', live: liveVersionRef.current, setAside: takenBack });
    if (takenBack) forget(null);
    setSelected(focusRegionId);
    setSelection(null);
    setExcludeText({});
    setOwnWork(null);
  }, [open, fileId, sheetName, focusRegionId]);

  // The saved plan changed while the workspace is open, not by this workspace's save.
  useEffect(() => {
    if (!open || ownWork?.processId === SEGMENT) return;
    dispatch({ type: 'saved-plan-changed', live: liveVersionRef.current });
  }, [open, ownWork, liveKey]);

  // This workspace's own gesture has been applied once its record carries a newer edit
  // than before and is no longer pending. A saved plan is then either Decode's active
  // version, and the workspace closes, or refused, and the draft is kept with the reason.
  useEffect(() => {
    if (!ownWork || ownWork.processed || !ownRecordProcessed) return;
    setOwnWork({ ...ownWork, processed: true });
    if (ownWork.processId !== SEGMENT) return;
    const live = liveVersionRef.current;
    const applied = live && live.made_by === 'person' && toMillis(live.made_at) >= ownWork.startedAt;
    if (applied) {
      hostRef.current.onClose();
      return;
    }
    const notes = fileRef.current?.processes?.[SEGMENT]?.dropped || [];
    const note = [...notes].reverse().find((candidate) => candidate.key === sheetName);
    dispatch({ type: 'refused', reason: note ? note.reason : REFUSED_WITHOUT_REASON });
  }, [ownWork, ownRecordProcessed, sheetName]);

  // The file stopped being editable. While the workspace's own gesture is being applied
  // that is its own work, done once the file can be edited again. Otherwise someone or
  // something else took the file out of editing: the workspace closes with a notice,
  // handing a draft with changes to the host first.
  const editableWhenLastIdle = useRef(!editBlock);
  useEffect(() => {
    if (!open) return;
    if (ownWork) {
      if (ownWork.processed && !editBlock) setOwnWork(null);
      return;
    }
    if (editBlock && editableWhenLastIdle.current) {
      const { onClose: close, onNotice: notify, onSetAside: keep } = hostRef.current;
      const current = stateRef.current;
      if (draftIsDirty(current)) {
        keep({ draft: current.draft, seed: current.seed });
        notify(`${editBlock} The ${sheetName} sheet was closed, and your unsaved changes there were kept: `
          + 'open the sheet again to take them back.');
      } else {
        notify(`${editBlock} The ${sheetName} sheet was closed.`);
      }
      close();
      return;
    }
    editableWhenLastIdle.current = !editBlock;
  }, [open, editBlock, ownWork, sheetName]);

  // The file's units, for each table's status and its ignore control. One equality filter.
  useEffect(() => {
    if (!open || !companyId || !tableId || !fileId) return undefined;
    setUnits({ status: 'loading', list: [] });
    const unitsQuery = query(
      collection(db, `users/${companyId}/tables/${tableId}/units`),
      where('file_id', '==', fileId),
    );
    return onSnapshot(unitsQuery, (snapshot) => {
      setUnits({ status: 'ready', list: snapshot.docs.map((unitDoc) => ({ id: unitDoc.id, ...unitDoc.data() })) });
    }, (error) => {
      console.error('Failed to watch the units of a file:', error);
      setUnits({ status: 'failed', list: [] });
    });
  }, [open, companyId, tableId, fileId]);

  const unitByRegionId = useMemo(() => {
    const byRegion = {};
    units.list.forEach((unit) => {
      if (unit.source?.sheet === sheetName && unit.source?.region_id) byRegion[unit.source.region_id] = unit;
    });
    return byRegion;
  }, [units.list, sheetName]);

  const ignoredRegionIds = useMemo(
    () => Object.values(unitByRegionId)
      .filter((unit) => unit.disposition === 'ignore')
      .map((unit) => unit.source.region_id),
    [unitByRegionId],
  );

  // The plan Decode discovered for this sheet: a process's own proposal is always its
  // decision's first version.
  const discoveredRegions = useMemo(() => {
    const byId = {};
    const discovered = segmentRecord ? versionsOf(segmentRecord, sheetName)[0] : undefined;
    (discovered?.value?.regions || []).forEach((region) => {
      byId[region.region_id] = region;
    });
    return byId;
  }, [segmentRecord, sheetName]);

  const problems = draft ? draftProblems(draft, extent?.lastRow || null) : [];
  const editingDisabled = Boolean(editBlock) || Boolean(ownWork) || saving || !draft;

  // An edit on an approved file, or a file in a batch, takes the file out of it: say so first.
  const withRewindConfirmation = (action) => {
    if (!needsRewindConfirmation(file)) {
      action();
      return;
    }
    setConfirm({ title: 'Change an approved file?', body: REWIND_CONFIRMATION, confirmLabel: 'Change it', onConfirm: action });
  };

  // This workspace's gesture on one of the file's records: sent, then followed until it
  // has been applied. A refusal ends it at once, with the reason.
  const sendOwnWork = async (processId, send) => {
    setOwnWork({
      processId,
      startedAt: Date.now(),
      previousEditedAt: toMillis(fileRef.current?.processes?.[processId]?.edited_at),
      processed: false,
    });
    try {
      await send();
      return true;
    } catch (error) {
      setOwnWork(null);
      setToast(gestureFailureMessage(error));
      return false;
    }
  };

  const updateRegion = (regionId, patch) => dispatch({ type: 'edit-region', regionId, patch });

  // A range dragged over the sheet: the focused table's bounds follow it, keeping the
  // header's height; with no table focused it is kept for "+ Add a table". A single cell
  // is a click, not a range.
  const handleSelectionChange = (range) => {
    if (editingDisabled) return;
    const isRange = range.rowLast > range.rowFirst || range.colLast > range.colFirst;
    const focusedRegion = (draft?.regions || []).find((region) => region.region_id === selected);
    if (!isRange || !focusedRegion) {
      setSelection(isRange ? range : null);
      return;
    }
    const patch = {
      col_first: columnIndexToLetter(range.colFirst),
      col_last: columnIndexToLetter(range.colLast),
    };
    if (range.rowLast > range.rowFirst) {
      const headerHeight = focusedRegion.header_last_row - focusedRegion.header_first_row;
      const extraHeaderRows = Number.isInteger(headerHeight) && headerHeight > 0 ? headerHeight : 0;
      const headerLast = Math.min(range.rowFirst + extraHeaderRows, range.rowLast - 1);
      patch.header_first_row = range.rowFirst;
      patch.header_last_row = headerLast;
      patch.body_first_row = headerLast + 1;
      patch.body_last_row = range.rowLast;
    }
    updateRegion(focusedRegion.region_id, patch);
    setSelection(null);
  };

  // A new table from the dragged range: its first row the header, the rest its body. A
  // range one row tall chooses the columns only, and the body defaults to two rows.
  const addTable = () => {
    if (!extent) return;
    const headerRow = selection ? selection.rowFirst : extent.firstRow;
    const bodyLast = selection && selection.rowLast > selection.rowFirst ? selection.rowLast : headerRow + 2;
    const region = {
      // A uuid4 hex, the id shape validate_person_plan requires of a drawn table.
      region_id: versionToken(),
      name: '',
      header_first_row: headerRow,
      header_last_row: headerRow,
      body_first_row: headerRow + 1,
      body_last_row: bodyLast,
      col_first: selection ? columnIndexToLetter(selection.colFirst) : 'A',
      col_last: columnIndexToLetter(selection ? selection.colLast : extent.lastCol),
      exclude_rows: [],
      section_values: {},
      unpivot: null,
      approximate: false,
    };
    dispatch({ type: 'add-region', region });
    setSelected(region.region_id);
    setSelection(null);
  };

  const setRowBound = (regionId, field, text) => {
    const parsed = text === '' ? null : Number(text);
    updateRegion(regionId, { [field]: Number.isFinite(parsed) ? parsed : null });
  };

  const save = () => {
    if (!draft || problems.length > 0 || !dirty) return;
    withRewindConfirmation(async () => {
      setSaving(true);
      const sent = await sendOwnWork(SEGMENT, () => editFileDecisions(
        companyId, tableId, fileId, SEGMENT,
        [{ key: sheetName, value: draftToPlan(draft), refersTo: null }],
        { expectedVersions: { [sheetName]: seed.madeAt } },
      ));
      // The draft stays either way: refused, it is still the person's to change; sent, it
      // is kept until Decode has applied it.
      if (sent) dispatch({ type: 'clear-notice' });
      setSaving(false);
    });
  };

  const readSheetAgain = () => setConfirm({
    title: 'Read this sheet again?',
    body: `Decode will read the ${sheetName} sheet again and find its tables from scratch. `
      + 'Its tables are created again, so the changes made to them (their columns, rules and classes) '
      + 'are lost, and anything unsaved here is discarded.',
    confirmLabel: 'Read it again',
    onConfirm: () => withRewindConfirmation(async () => {
      const sent = await sendOwnWork(SEGMENT, () => requestFile(companyId, tableId, fileId, SEGMENT, 'start_over', [sheetName]));
      if (sent) onClose();
    }),
  });

  const changeDisposition = (unit, value) => {
    const othersIngest = Object.entries(file?.units || {})
      .some(([unitId, entry]) => unitId !== unit.id && entry.disposition === 'ingest');
    if (value === 'ignore' && !othersIngest) {
      setNothingToIngestOpen(true);
      return;
    }
    withRewindConfirmation(() => sendOwnWork(DISPOSITIONS, () => setDisposition(companyId, tableId, fileId, unit.id, value)));
  };

  // A save being applied is no longer the person's to lose: the outcome shows under
  // Sheets and tables whether or not the workspace stays open.
  const requestClose = () => {
    if (!dirty || ownWork?.processId === SEGMENT) {
      onClose();
      return;
    }
    setConfirm({
      title: 'Discard your changes?',
      body: 'The tables and fields you changed on this sheet are lost if you close now.',
      confirmLabel: 'Discard',
      onConfirm: onClose,
    });
  };

  const runConfirmed = () => {
    const action = confirm.onConfirm;
    setConfirm(null);
    action();
  };

  const renderRegionCard = (region, index) => {
    const unit = unitByRegionId[region.region_id];
    const colour = regionColour(index);
    const isFocused = selected === region.region_id;
    const discovered = discoveredRegions[region.region_id];
    const differsFromDiscovered = discovered && JSON.stringify(discovered) !== JSON.stringify(region);
    const ignored = unit?.disposition === 'ignore';

    return (
      <Box
        key={region.region_id}
        data-testid={`table-card-${index + 1}`}
        onClick={() => setSelected(isFocused ? null : region.region_id)}
        sx={{
          border: '1.5px solid',
          borderColor: isFocused ? colour : 'divider',
          borderStyle: ignored ? 'dashed' : 'solid',
          borderRadius: 1.5,
          p: 1.25,
          bgcolor: 'background.paper',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: colour, flex: 'none' }} />
          <Typography variant="body2" fontWeight={700}>{`Table ${index + 1}`}</Typography>
          {region.name && <Typography variant="caption" color="text.secondary">{region.name}</Typography>}
          {unit && (
            <Chip
              size="small"
              variant="outlined"
              label={{ ignore: 'Ignored', context: 'Header values' }[unit.disposition] || UNIT_LABEL[unit.status] || unit.status}
            />
          )}
          {!unit && units.status === 'ready' && <Chip size="small" color="secondary" variant="outlined" label="new" />}
          <Box sx={{ flex: 1 }} />
          {differsFromDiscovered && (
            <Tooltip title="Back to what Decode found">
              <span>
                <Button
                  size="small"
                  disabled={editingDisabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    dispatch({ type: 'replace-region', region: discovered });
                  }}
                  sx={{ textTransform: 'none', minWidth: 0 }}
                >
                  Undo
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            size="small"
            color="error"
            disabled={editingDisabled}
            onClick={(event) => {
              event.stopPropagation();
              dispatch({ type: 'remove-region', regionId: region.region_id });
              if (selected === region.region_id) setSelected(null);
            }}
            sx={{ textTransform: 'none', minWidth: 0 }}
          >
            Remove
          </Button>
        </Box>

        {!isFocused && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {`rows ${region.body_first_row}–${region.body_last_row}, columns ${region.col_first}–${region.col_last}`}
            {(region.exclude_rows || []).length ? `, ${region.exclude_rows.length} left out` : ''}
          </Typography>
        )}

        {isFocused && (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}
          >
            <TextField
              label="Name"
              size="small"
              value={region.name || ''}
              disabled={editingDisabled}
              onChange={(event) => updateRegion(region.region_id, { name: event.target.value })}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label="Header starts"
              size="small"
              type="number"
              value={region.header_first_row ?? ''}
              disabled={editingDisabled}
              onChange={(event) => setRowBound(region.region_id, 'header_first_row', event.target.value)}
            />
            <TextField
              label="Header ends"
              size="small"
              type="number"
              value={region.header_last_row ?? ''}
              disabled={editingDisabled}
              onChange={(event) => setRowBound(region.region_id, 'header_last_row', event.target.value)}
            />
            <TextField
              label="Rows from"
              size="small"
              type="number"
              value={region.body_first_row ?? ''}
              disabled={editingDisabled}
              onChange={(event) => setRowBound(region.region_id, 'body_first_row', event.target.value)}
            />
            <TextField
              label="Rows to"
              size="small"
              type="number"
              value={region.body_last_row ?? ''}
              disabled={editingDisabled}
              onChange={(event) => setRowBound(region.region_id, 'body_last_row', event.target.value)}
            />
            <TextField
              label="Columns from"
              size="small"
              value={region.col_first || ''}
              disabled={editingDisabled}
              onChange={(event) => updateRegion(region.region_id, { col_first: event.target.value.trim().toUpperCase() })}
            />
            <TextField
              label="Columns to"
              size="small"
              value={region.col_last || ''}
              disabled={editingDisabled}
              onChange={(event) => updateRegion(region.region_id, { col_last: event.target.value.trim().toUpperCase() })}
            />
            <TextField
              label="Rows to leave out"
              size="small"
              placeholder="for example 7, 9"
              value={excludeText[region.region_id] ?? (region.exclude_rows || []).join(', ')}
              disabled={editingDisabled}
              onChange={(event) => {
                const text = event.target.value;
                setExcludeText((previous) => ({ ...previous, [region.region_id]: text }));
                if (!/[,\s]$/.test(text)) updateRegion(region.region_id, { exclude_rows: parseRowList(text) });
              }}
              onBlur={() => {
                const text = excludeText[region.region_id];
                if (text === undefined) return;
                updateRegion(region.region_id, { exclude_rows: parseRowList(text) });
                setExcludeText((previous) => {
                  const next = { ...previous };
                  delete next[region.region_id];
                  return next;
                });
              }}
              sx={{ gridColumn: '1 / -1' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
              Or drag over the sheet to set the rows and columns.
            </Typography>
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {region.unpivot && (
                <Tooltip title="Keep the period columns as they are instead of turning them into rows">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={editingDisabled}
                      onClick={() => updateRegion(region.region_id, { unpivot: null })}
                      sx={{ textTransform: 'none' }}
                    >
                      Keep columns as they are
                    </Button>
                  </span>
                </Tooltip>
              )}
              {unit && (
                <Button
                  size="small"
                  variant="outlined"
                  color={ignored ? 'primary' : 'error'}
                  disabled={Boolean(editBlock) || Boolean(ownWork)}
                  onClick={() => changeDisposition(unit, ignored ? 'ingest' : 'ignore')}
                  sx={{ textTransform: 'none' }}
                >
                  {ignored ? 'Use this table' : 'Ignore this table'}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const regions = draft ? draft.regions : NO_REGIONS;
  const fieldsFocused = selected === 'fields';

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth={false}
        PaperProps={{ sx: { width: '95vw', maxWidth: '95vw', height: '94vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25 }}>
          <Typography variant="subtitle1" component="span" fontWeight={700}>{sheetName}</Typography>
          <Typography variant="body2" component="span" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
            {`${file?.name || ''} · nothing changes until you save`}
          </Typography>
          {dirty && !ownWork && <Chip size="small" color="info" variant="outlined" label="unsaved" />}
          <IconButton size="small" aria-label="Close" onClick={requestClose}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minWidth: 0, p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {editBlock && (
              <Alert severity="info">
                {dirty ? `${editBlock} Your changes are kept here; save them when it finishes.` : editBlock}
              </Alert>
            )}
            {ownWork?.processId === SEGMENT && !ownWork.processed && (
              <Alert severity="info" icon={<CircularProgress size={18} />}>
                Decode is applying this sheet&apos;s tables. If it cannot use them, the reason appears here.
              </Alert>
            )}
            {sheetCopyPath ? (
              <SheetViewer
                companyId={companyId}
                tableId={tableId}
                fileId={fileId}
                sheetCopyPath={sheetCopyPath}
                sheetName={sheetName}
                regions={regions}
                ignoredRegionIds={ignoredRegionIds}
                focus={selected}
                onSelectionChange={handleSelectionChange}
                onSheetLoad={setExtent}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                There is no copy of this sheet to show.
              </Typography>
            )}
          </Box>

          <Box sx={{
            width: 380,
            flex: 'none',
            borderLeft: '1px solid',
            borderColor: 'divider',
            overflowY: 'auto',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            bgcolor: 'background.default',
          }}
          >
            {notice && (
              <Alert
                severity={notice.severity}
                onClose={() => dispatch({ type: 'clear-notice' })}
                action={notice.offerReload ? (
                  <Button color="inherit" size="small" onClick={() => dispatch({ type: 'reload', live: liveVersionRef.current })}>
                    Reload
                  </Button>
                ) : null}
              >
                {notice.text}
              </Alert>
            )}
            {units.status === 'failed' && (
              <Alert severity="error">The tables&apos; status could not be loaded. Close the sheet and open it again.</Alert>
            )}
            {!draft && (
              <Typography variant="body2" color="text.secondary">Decode has not read this sheet yet.</Typography>
            )}

            {draft && (
              <>
                <Typography variant="overline" color="text.secondary">Tables on this sheet</Typography>
                {regions.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No tables on this sheet.</Typography>
                )}
                {regions.map((region, index) => renderRegionCard(region, index))}

                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  disabled={editingDisabled || !extent}
                  onClick={addTable}
                  sx={{ textTransform: 'none', borderStyle: 'dashed' }}
                >
                  {selection
                    ? `Add a table (rows ${selection.rowFirst}–${selection.rowLast}, `
                      + `${columnIndexToLetter(selection.colFirst)}–${columnIndexToLetter(selection.colLast)})`
                    : 'Add a table'}
                </Button>

                <Box
                  onClick={() => setSelected(fieldsFocused ? null : 'fields')}
                  sx={{
                    border: '1.5px solid',
                    borderColor: fieldsFocused ? 'info.main' : 'divider',
                    borderRadius: 1.5,
                    p: 1.25,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="body2" fontWeight={700}>Header fields</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {regions.length ? 'used by the tables on this sheet' : 'used by the other sheets'}
                    </Typography>
                  </Box>
                  <Box onClick={(event) => event.stopPropagation()} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
                    {draft.fields.map((field) => (
                      <Box key={field.rowId} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          placeholder="Name"
                          inputProps={{ 'aria-label': 'Header field name' }}
                          value={field.key}
                          disabled={editingDisabled}
                          onChange={(event) => dispatch({ type: 'edit-field', rowId: field.rowId, patch: { key: event.target.value } })}
                          sx={{ width: '40%' }}
                        />
                        <TextField
                          size="small"
                          placeholder="Value"
                          inputProps={{ 'aria-label': 'Header field value' }}
                          value={String(field.value ?? '')}
                          disabled={editingDisabled}
                          onChange={(event) => dispatch({ type: 'edit-field', rowId: field.rowId, patch: { value: event.target.value } })}
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          aria-label="Remove this field"
                          disabled={editingDisabled}
                          onClick={() => dispatch({ type: 'remove-field', rowId: field.rowId })}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      disabled={editingDisabled}
                      onClick={() => dispatch({ type: 'add-field' })}
                      sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                      + Add a field
                    </Button>
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Click a table to focus it. Drag over the sheet to draw a new one.
                </Typography>

                {problems.length > 0 && (
                  <Box role="alert">
                    {problems.map((problem) => (
                      <Typography key={problem} variant="caption" color="error" sx={{ display: 'block' }}>
                        {problem}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Button
                  size="small"
                  variant="outlined"
                  disabled={Boolean(editBlock) || Boolean(ownWork) || saving}
                  onClick={readSheetAgain}
                  sx={{ textTransform: 'none', alignSelf: 'flex-start', mt: 1 }}
                >
                  Read this sheet again
                </Button>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1, gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', border: `2px solid ${regionColour(0)}` }} />
              <Typography variant="caption" color="text.secondary">table</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: withAlpha(regionColour(0), 0.25) }} />
              <Typography variant="caption" color="text.secondary">header</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', border: `2px dashed ${regionColour(0)}` }} />
              <Typography variant="caption" color="text.secondary">ignored</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              greyed: outside the focused table, or left out of it
            </Typography>
          </Box>
          <Button onClick={requestClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!dirty || problems.length > 0 || editingDisabled}
            onClick={save}
            sx={{ textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm?.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={runConfirmed} sx={{ textTransform: 'none' }}>
            {confirm?.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={nothingToIngestOpen} onClose={() => setNothingToIngestOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent>
          <DialogContentText>{NO_INGEST_UNIT_LEFT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNothingToIngestOpen(false)} sx={{ textTransform: 'none' }}>Dismiss</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast('')}>
        <Alert severity="error" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
